// 맵 비주얼: 환경 아트(env/: Tripo 키트·손그림 바닥·고원·원경·효과) + 키트가 다 읽히기 전까지 보이는 그레이박스.
// 맵마다 한 번만 만들고 렌더러가 캐시해 재사용한다(재생성·재질 누수 없음). 충돌·길찾기는 levels.ts 격자 그대로다.
import * as THREE from 'three/webgpu';
import { PALETTE, type QualityLevel } from '../shared/config';
import { Cell, isRegionId, type MapDef, type MapGrid } from '../shared/levels';
import { REGIONS } from '../shared/regions';
import { buildHazardPools } from './env/hazardPools';
import { cutPositionNode } from './env/cut';
import { labelSprite } from './env/label';
import { EnvVisual } from './env/index';
import { StageEnv } from './env/stageEnv';
import { TownEnv } from './env/townEnv';
import { gateFront, townNoOfMap } from '../shared/villageMaps';
import { loadTexture, track } from './env/kits';
import { gateMaterial } from './env/surfaces';
import { buildEmberTerrain, emberFloorMaterial } from './env/emberTerrain';
import { chasmAt } from './env/emberLayout';
import { colored, merge } from './geo';

export { CUT_H, cutFactor, setCutawayFocus } from './env/cut';

export interface MapVisual {
  readonly group: THREE.Group;
  /** map.gates 순서의 문 메시(높이 wallHeight 기준 가운데 원점 상자: 렌더가 열림·컷에 따라 y 크기를 바꾼다) */
  readonly gateMeshes: THREE.Mesh[];
  /**
   * 환경 키트·텍스처 로드를 시작한다(여러 번 불러도 한 번만). 끝나도 환경은 숨긴 채로 둔다:
   * 미리 컴파일한 뒤 reveal()로 그레이박스와 한 번에 바꿔야 플레이 중 교체 때 셰이더 컴파일 끊김이 없다.
   * 실패해도 resolve한다(그레이박스 유지).
   */
  load(): Promise<void>;
  /** load가 끝난 환경을 보이고 대신하는 그레이박스를 숨긴다. */
  reveal(): void;
  /** 'idle' 시작 전, 'loading', 'loaded' 숨긴 채 준비됨, 'ready' 보이는 중 */
  readonly envState: 'idle' | 'loading' | 'loaded' | 'ready';
  /** 미리 컴파일용: true면 그룹·환경을 보이게 하고 모든 메시의 시야 컬링을 끈다, false면 원래대로 */
  compileView(on: boolean): void;
  /** 매 프레임(카메라 갱신 뒤): 화면 밖 키트 인스턴스를 뺀다. 카메라가 거의 그대로면 아무것도 하지 않는다. */
  update(camera: THREE.PerspectiveCamera): void;
  dispose(): void;
}

export interface BuildMapOptions {
  quality?: QualityLevel;
}

/** 병합 전 조각에 블록 중심과 컷 허용 여부를 정점 속성으로 넣는다. */
function withCut(g: THREE.BufferGeometry, cx: number, cz: number, allow: number): THREE.BufferGeometry {
  const n = g.getAttribute('position').count;
  const a = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { a[i * 3] = cx; a[i * 3 + 1] = cz; a[i * 3 + 2] = allow; }
  g.setAttribute('aCut', new THREE.BufferAttribute(a, 3));
  return g;
}

const LANDMARK_COLOR: Record<string, number> = {
  fountain: PALETTE.skyTeal,
  shop: 0xb08a5a,
  forge: 0x8a5a3a,
  trainer: 0x7a8a5a,
  board: 0xa07a4a,
  gate: 0x6a6a7a,
  pillar: 0x9a9488,
  rubble: 0x8a857a,
  statue: 0x8aa080,
  tree: PALETTE.moss,
};

/** 그레이박스 색: 마을은 따뜻한 돌, 이끼 유적은 차가운 회녹색 돌 */
const THEME = {
  village: { floorA: 0xe9d8b4, floorB: 0xd8c59f, wall: 0xa8967a },
  r1: { floorA: 0xa9ad98, floorB: 0x969b86, wall: 0x7d8470 },
  r2: { floorA: 0x403639, floorB: 0x30282c, wall: 0x29242a },
  // D3a: 서리 묘지(청회 돌·눈), 가라앉은 사원(이끼 진흙)
  r3: { floorA: 0xb9c4d2, floorB: 0xa3afbf, wall: 0x6f7a8c },
  r4: { floorA: 0x7d8a6a, floorB: 0x6b7659, wall: 0x4f5a44 },
  // C2: 황금 모래 폐도(사암·모래), 심연의 성채(흑요석)
  r5: { floorA: 0xd6b680, floorB: 0xc4a26c, wall: 0x9a7a4c },
  r6: { floorA: 0x3e3650, floorB: 0x332c44, wall: 0x241e32 },
} as const;
/** 지역 문 포털 색·이름표 색(D3a: 지역마다) */
const EXIT_STYLE: Readonly<Record<string, { portal: number; label: string }>> = {
  r1: { portal: PALETTE.skyTeal, label: '#4FB6D0' },
  r2: { portal: 0xff5018, label: '#FFAF70' },
  r3: { portal: 0x8fd8ff, label: '#BDE6FF' },
  r4: { portal: 0x8fe05a, label: '#C8F59A' },
  r5: { portal: 0xffc24a, label: '#FFE09A' },
  r6: { portal: 0xc060ff, label: '#E2B4FF' },
};

function checkerTexture(a: number, b: number): THREE.DataTexture {
  const data = new Uint8Array(16);
  // sRGB 텍스처이므로 16진 색의 sRGB 바이트를 그대로 쓴다
  const px = (hex: number, o: number) => {
    data[o] = (hex >> 16) & 255; data[o + 1] = (hex >> 8) & 255; data[o + 2] = hex & 255; data[o + 3] = 255;
  };
  px(a, 0); px(b, 4); px(b, 8); px(a, 12);
  const tex = new THREE.DataTexture(data, 2, 2, THREE.RGBAFormat);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/** 문짝 높이 배율: 아치 개구부를 채우도록 wallHeight보다 키우고 아래는 바닥 밑으로 묻는다 */
const GATE_TALL = 1.6;

export function buildMap(map: MapDef, grid: MapGrid, opts: BuildMapOptions = {}): MapVisual {
  const theme = THEME[map.id];
  const group = new THREE.Group();
  group.name = 'map-' + map.id;
  const gateMeshes: THREE.Mesh[] = [];
  const owned: { dispose(): void }[] = [];

  // ── 그레이박스(대체물) ──
  const floorParts: THREE.BufferGeometry[] = [];
  for (const f of map.floors) {
    const w = f.x1 - f.x0, d = f.z1 - f.z0;
    const geo = new THREE.PlaneGeometry(w, d);
    geo.rotateX(-Math.PI / 2);
    const uv = geo.getAttribute('uv') as THREE.BufferAttribute;
    for (let i = 0; i < uv.count; i++) uv.setXY(i, (uv.getX(i) * w + f.x0) / 2, (uv.getY(i) * d - f.z1) / 2);
    geo.translate((f.x0 + f.x1) / 2, 0, (f.z0 + f.z1) / 2);
    floorParts.push(geo);
  }
  const checker = checkerTexture(theme.floorA, theme.floorB);
  owned.push(checker);
  const floor = new THREE.Mesh(mergeAll(floorParts), new THREE.MeshStandardMaterial({ map: checker, roughness: 1 }));
  floor.receiveShadow = true;
  group.add(floor);

  // 벽: 바닥에 맞닿은 빈 셀마다 블록 1개(인스턴싱)
  const wallCells: number[] = [];
  // 스테이지 지도(S2)는 벽이 없다: 가장자리는 낭떠러지·물이다(환경이 읽히기 전에는 바닥만 보인다)
  for (let z = 0; z < (map.stageKey ? 0 : grid.height); z++) {
    for (let x = 0; x < grid.width; x++) {
      if (grid.cells[z * grid.width + x] !== Cell.Void) continue;
      let touches = false;
      for (let dz = -1; dz <= 1 && !touches; dz++) {
        for (let dx = -1; dx <= 1; dx++) {
          const nx = x + dx, nz = z + dz;
          if (nx < 0 || nz < 0 || nx >= grid.width || nz >= grid.height) continue;
          if (grid.cells[nz * grid.width + nx] !== Cell.Void) { touches = true; break; }
        }
      }
      if (touches && !chasmAt(map, grid, grid.originX + x + 0.5, grid.originZ + z + 0.5)) wallCells.push(x, z);
    }
  }
  const wallCount = Math.max(1, wallCells.length / 2);
  const wallGeo = new THREE.BoxGeometry(1, map.wallHeight, 1);
  wallGeo.translate(0, map.wallHeight / 2, 0);
  const wallCut = new Float32Array(wallCount * 3);
  wallGeo.setAttribute('aCut', new THREE.InstancedBufferAttribute(wallCut, 3));
  const wallMat = new THREE.MeshStandardNodeMaterial({ color: theme.wall, roughness: 1 });
  wallMat.positionNode = cutPositionNode();
  const walls = new THREE.InstancedMesh(wallGeo, wallMat, wallCount);
  const mtx = new THREE.Matrix4();
  for (let i = 0; i < wallCells.length; i += 2) {
    const wx = grid.originX + wallCells[i] + 0.5, wz = grid.originZ + wallCells[i + 1] + 0.5;
    mtx.makeTranslation(wx, 0, wz);
    walls.setMatrixAt(i / 2, mtx);
    wallCut[(i / 2) * 3] = wx; wallCut[(i / 2) * 3 + 1] = wz; wallCut[(i / 2) * 3 + 2] = 1;
  }
  walls.count = wallCells.length / 2;
  walls.castShadow = true;
  walls.receiveShadow = true;
  walls.frustumCulled = false;
  group.add(walls);
  if (map.id === 'r2' && !map.stageKey) { // 지역2 스테이지 지도(S6)는 StageEnv가 용암 바다·위험 칸을 그린다
    floor.visible = false;
    group.add(buildEmberTerrain(map, grid));
  }
  // 서리 균열·독 늪(D3a)·유사·심연 균열(C2): 웅덩이 칸을 빛나는 판으로 덮는다(판정 칸과 같은 사각형 — 보이는 곳이 위험한 곳)
  if (map.lava && map.lava.length > 0 && (map.hazardTheme === 'frost' || map.hazardTheme === 'venom' || map.hazardTheme === 'sand' || map.hazardTheme === 'void')) {
    const pools = buildHazardPools(map.lava, map.hazardTheme);
    group.add(pools);
  }

  // 랜드마크: 랜드마크마다 한 메시(키트로 바뀐 것만 숨긴다)
  const lmMat = new THREE.MeshStandardNodeMaterial({ color: 0xffffff, roughness: 1, vertexColors: true });
  lmMat.positionNode = cutPositionNode();
  const lmMeshes: THREE.Mesh[] = [];
  const labels = new Map<number, THREE.Sprite>();
  map.landmarks.forEach((l, i) => {
    const w = l.rect.x1 - l.rect.x0, d = l.rect.z1 - l.rect.z0;
    const cx = (l.rect.x0 + l.rect.x1) / 2, cz = (l.rect.z0 + l.rect.z1) / 2;
    const parts: THREE.BufferGeometry[] = [];
    if (l.kind === 'tree') {
      parts.push(withCut(colored(new THREE.CylinderGeometry(0.35, 0.5, l.height * 0.55, 8), 0x6b4a2a, cx, l.height * 0.275, cz), cx, cz, 0));
      parts.push(withCut(colored(new THREE.SphereGeometry(Math.max(w, d) * 1.1, 12, 10), PALETTE.moss, cx, l.height * 0.75, cz), cx, cz, 0));
    } else {
      parts.push(withCut(colored(new THREE.BoxGeometry(w, l.height, d), LANDMARK_COLOR[l.kind] ?? 0x888888, cx, l.height / 2, cz), cx, cz, 1));
    }
    const m = new THREE.Mesh(mergeAll(parts), lmMat);
    m.castShadow = true;
    m.receiveShadow = true;
    group.add(m);
    lmMeshes.push(m);
    // 이름표는 마을 시설에만(던전 장식 이름표는 카메라 가까이에서 화면을 가린다)
    // 분수는 광장 한가운데 낮은 장식이라 이름표가 스폰 바로 앞에서 카엘 머리를 가린다(parent 화면 검토 2026-10-15). 시설·NPC 이름표만 띄운다
    // 의뢰 게시판은 마을 북쪽 끝에 있어 이름표가 화면 위쪽 가장자리에 떠 HUD 메뉴 줄과 겹쳐 보였다(대표님 2026-10-27 18:54 "의뢰 게시판 같은 건 설정에다").
    // 게시판 시설(모형·충돌)과 옆 의뢰 NPC 대화는 그대로 두고 떠 있는 이름표만 뺀다. 진입은 톱니 → 일시정지 창 「의뢰 게시판」(ui/screens.ts)
    if (l.label && map.id === 'village' && l.kind !== 'fountain' && l.kind !== 'gate' && l.kind !== 'board') {
      const sp = labelSprite(l.label, '#F2C14E', 3.6);
      sp.position.set(cx, l.height + 1.4, cz);
      group.add(sp);
      labels.set(i, sp);
    }
  });

  // 문: 열림 값에 따라 렌더가 y 크기를 조절한다(가운데 원점, 기준 높이 wallHeight). 실제 판은 GATE_TALL배라 아치 개구부를 채운다.
  const gateMat0 = new THREE.MeshStandardMaterial({ color: 0x5a4a3a, roughness: 0.8 });
  for (const g of map.gates) {
    const w = g.rect.x1 - g.rect.x0, d = g.rect.z1 - g.rect.z0;
    const m = new THREE.Mesh(new THREE.BoxGeometry(w + 0.2, map.wallHeight * GATE_TALL, d * 0.7), gateMat0);
    m.position.set((g.rect.x0 + g.rect.x1) / 2, map.wallHeight / 2, (g.rect.z0 + g.rect.z1) / 2);
    m.castShadow = true;
    gateMeshes.push(m);
    group.add(m);
  }

  // 출구: 청록 포털 판 + 이름표('던전 입구: 이끼 유적')
  const exitLabels: THREE.Sprite[] = [];
  /** 이름표마다 붙은 문 랜드마크 인덱스(같은 이름의 문, 없으면 x가 겹치는 문 — 게이트 모델 높이를 읽는다) */
  const exitGate: number[] = [];
  /** 동·서를 보는 문의 이름표는 게이트 위로 올리지 않는다(카메라가 북쪽을 봐서 가까운 높은 이름표는 화면 위로 잘린다 — 문 앞 낮은 자리에 둔다) */
  const exitLift: boolean[] = [];
  for (const ex of map.exits) {
    const w = ex.rect.x1 - ex.rect.x0, d = ex.rect.z1 - ex.rect.z0;
    const geo = new THREE.PlaneGeometry(w, d);
    geo.rotateX(-Math.PI / 2);
    const style = EXIT_STYLE[ex.to] ?? EXIT_STYLE.r1;
    const portal = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: style.portal, transparent: true, opacity: 0.4, depthWrite: false }));
    portal.position.set((ex.rect.x0 + ex.rect.x1) / 2, 0.04, (ex.rect.z0 + ex.rect.z1) / 2);
    group.add(portal);
    const tier = isRegionId(ex.to) ? REGIONS[ex.to].tier : '입문';
    const sp = labelSprite(ex.label + ' · ' + tier, style.label, 6.4, 'exit');
    // 이름표는 출구의 문 반대쪽 가장자리(문 앞, 카메라 쪽) 위. 문 정면이 동·서·북을 봐도 문 앞에 뜬다(마을②~④, STAGE-WORLD-PRD 5.4)
    let gi = map.landmarks.findIndex((l) => l.kind === 'gate' && l.label === ex.label);
    if (gi < 0) gi = map.landmarks.findIndex((l) => l.kind === 'gate' && portal.position.x >= l.rect.x0 && portal.position.x <= l.rect.x1);
    const front = gi >= 0 ? gateFront(map.floors, map.landmarks[gi].rect) : 0;
    const fx = Math.round(Math.sin(front)), fz = Math.round(Math.cos(front));
    sp.position.set(portal.position.x + (fx * w) / 2, 4.2, portal.position.z + (fz * d) / 2);
    group.add(sp);
    exitLabels.push(sp);
    exitGate.push(gi);
    exitLift.push(fx === 0);
  }

  // ── 환경 아트: 다 읽히면 그레이박스를 숨긴다 ──
  // 마을②~④(S7)는 각자 지도용 TownEnv(용암·바다·늪 위 판), 스테이지 지도는 StageEnv, 나머지(마을①·자유 입장 지역)는 EnvVisual
  const town = townNoOfMap(map);
  const env: EnvVisual | StageEnv | TownEnv = map.stageKey ? new StageEnv(map, grid, opts.quality ?? 'high') : town ? new TownEnv(map, grid, opts.quality ?? 'high', town) : new EnvVisual(map, grid, opts.quality ?? 'high');
  env.root.visible = false;
  group.add(env.root);
  let envState: MapVisual['envState'] = 'idle';
  let applyEnv: (() => void) | null = null;
  let loading: Promise<void> | null = null;
  const load = (): Promise<void> => {
    if (loading) return loading;
    envState = 'loading';
    const gateTex = map.gates.length > 0 ? track(loadTexture('r1/tex/floor_moss_stone.webp')) : Promise.resolve(null);
    loading = Promise.all([env.start(), gateTex]).then(([r, tex]) => {
      // 미리 컴파일하기 전에 R2 임시 바닥을 기존 판석 텍스처 재질로 바꾼다.
      if (map.id === 'r2' && !map.stageKey) {
        const basalt = group.getObjectByName('basalt-floor') as THREE.Mesh;
        (basalt.material as THREE.Material).dispose();
        basalt.material = emberFloorMaterial(map, tex);
      }
      applyEnv = () => swap(r, tex);
      envState = 'loaded';
    }).catch((err) => {
      console.warn('환경 아트 구성 실패(그레이박스 유지)', err);
      envState = 'loaded';
    });
    return loading;
  };
  const swap = (r: Awaited<ReturnType<(EnvVisual | StageEnv | TownEnv)['start']>>, tex: THREE.Texture | null): void => {
    if (r.floor) floor.visible = false;
    if (r.walls) walls.visible = false;
    for (const i of r.replaced) lmMeshes[i].visible = false;
    for (const [i, sp] of labels) {
      const t = r.tops.get(i);
      if (t !== undefined) sp.position.y = Math.max(sp.position.y, t + 1.0);
    }
    // 출구 이름표는 게이트 모델 위로
    for (let k = 0; k < exitLabels.length; k++) {
      const sp = exitLabels[k];
      const gt = r.tops.get(exitGate[k]);
      if (gt !== undefined && exitLift[k]) sp.position.y = Math.max(sp.position.y, gt + 1.2);
    }
    if (gateMeshes.length > 0) {
      const gm = gateMaterial(tex, map.id === 'r2', map.stageKey?.startsWith('m') ? 'wood' : map.stageKey === 'f' ? 'bark' : 'stone');
      owned.push(gm);
      for (const m of gateMeshes) m.material = gm;
    }
    env.root.visible = true;
  };

  const saved = { visible: false, envVisible: false, culled: [] as THREE.Object3D[] };
  return {
    group,
    gateMeshes,
    load,
    reveal() {
      if (envState !== 'loaded') return;
      applyEnv?.();
      applyEnv = null;
      envState = 'ready';
    },
    get envState() {
      return envState;
    },
    compileView(on: boolean) {
      if (on) {
        saved.visible = group.visible;
        saved.envVisible = env.root.visible;
        saved.culled.length = 0;
        group.traverse((o) => { if (o.frustumCulled) { saved.culled.push(o); o.frustumCulled = false; } });
        group.visible = true;
        env.root.visible = true;
        env.cull(null);
      } else {
        for (const o of saved.culled) o.frustumCulled = true;
        saved.culled.length = 0;
        group.visible = saved.visible;
        env.root.visible = saved.envVisible;
      }
    },
    update(camera: THREE.PerspectiveCamera) {
      if (envState === 'ready') {
        env.cull(camera);
        env.animate(performance.now());
      }
    },
    dispose() {
      env.dispose();
      const mats = new Set<THREE.Material>();
      group.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.geometry) m.geometry.dispose();
        const mat = m.material as THREE.Material | THREE.Material[] | undefined;
        if (Array.isArray(mat)) mat.forEach((x) => mats.add(x));
        else if (mat) mats.add(mat);
      });
      mats.forEach((m) => {
        (m as THREE.SpriteMaterial).map?.dispose();
        m.dispose();
      });
      for (const o of owned) o.dispose();
    },
  };
}

function mergeAll(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  return parts.length === 1 ? parts[0] : merge(parts);
}
